import {
  ExportResponse,
  ValidationError,
} from '@givz/core-client/dist/rest-client';
import { LoadingPageType } from '@components/ui/loading-skeleton';
import { useModal } from '@hooks';
import { useImpersonate } from '@hooks/impersonate';
import {
  Button,
  Card,
  DataTable,
  DataTableProps,
  Filters,
  FiltersProps,
  Heading,
  Pagination,
  PaginationProps,
} from '@shopify/polaris';
import { EmptyState, LoadingSkeleton, Select, TagsInput } from '@ui';
import { validateEmail } from '@utils/validation';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SpaceBetweenRowContainer } from '../container/common';
import { EmptyStateProps } from '../empty-state';
import { StyledElement } from '../types';

const DataTableContainer = styled.div<{ clickable?: boolean }>`
  margin-top: 1.25rem;
  padding-top: 1rem;

  .Polaris-Heading {
    padding: 2rem;
  }

  .Polaris-DataTable__Cell--firstColumn {
    min-width: 125px;
  }
`;

const TableContainer = styled.div``;
const TableFooter = styled(SpaceBetweenRowContainer)`
  margin-top: 2rem;
`;
const FooterCell = styled.div`
  flex: 1;
  align-items: center;
  justify-content: center;
  display: flex;

  :first-child {
    justify-content: left !important;
  }

  :last-child {
    justify-content: right !important;
  }

  nav[aria-label='Pagination'],
  .Polaris-Button {
    background-color: #ffffff;
    border-radius: 4px;
  }
`;

const HeadContainer = styled.div`
  display: flex;
  justify-content: space-between;
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const FilterContainer = styled.div`
  min-width: 350px;
  margin-top: 1.25rem;
  margin-right: 2rem;
  div {
    justify-content: flex-end;
  }

  .Polaris-Filters__TagsContainer > * {
    margin-right: 0;
  }

  @media (max-width: 992px) {
    min-width: auto;
    margin-top: 0;
    margin-left: 2rem;
    div {
      justify-content: flex-start;
    }
  }
`;

export type TableProps = {
  filterProps?: FiltersProps;
  emptyState: EmptyStateProps;
  loading: boolean;
  pageResultsProps: {
    pageSize: string;
    onPageSizeChange: (pageSize: string) => void;
  };
  onExport?: (
    emails: string[],
  ) => Promise<
    { data: ExportResponse; errors: ValidationError[] | null } | undefined
  >;
  paginationProps: PaginationProps;
  exportEmail?: string;
  skeletonTitle: string;
  title: React.ReactNode;
} & StyledElement &
  DataTableProps;

const pageSizeOptions = ['10', '20', '50', '100'];

const Table = ({
  className,
  emptyState,
  exportEmail,
  filterProps,
  loading,
  onExport,
  pageResultsProps,
  paginationProps,
  skeletonTitle,
  title,
  ...tableProps
}: TableProps) => {
  const modal = useModal();
  const { isLoggedIn: impersonateIsLoggedIn } = useImpersonate();
  const [pageSize, setPageSize] = useState(pageResultsProps.pageSize);
  const [emails, setEmails] = useState<string[]>([]);
  const [exported, setExported] = useState<boolean>(false);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);

  const onExportEmails = useCallback(async () => {
    if (onExport) {
      setExported(true);
      setLoadingExport(true);
      const onExportResponse = await onExport(emails);
      setLoadingExport(false);
      setEmails([]);
      modal.display(
        () => (
          <div>
            {onExportResponse?.data?.url && (
              <>
                Your download is available.{' '}
                <Link href={onExportResponse?.data?.url}>
                  <a>Download Now</a>
                </Link>
                . Downloads are available for 7 days.
              </>
            )}
            {!onExportResponse?.data?.url &&
              (!onExportResponse?.errors ||
                (onExportResponse?.errors &&
                  onExportResponse?.errors?.length === 0)) && (
                <>
                  We are generating your CSV and will email the file shortly
                  when it is complete
                </>
              )}

            {onExportResponse?.errors &&
              onExportResponse?.errors?.length > 0 && (
                <>There was an issue, please try again</>
              )}
          </div>
        ),
        {
          primaryAction: {
            content: 'Dismiss',
            onAction: onResetExportEmails,
          },
          title: 'Export to csv',
        },
      );
    }
  }, [emails, modal, onExport]);

  useEffect(() => {
    modal?.updateProps({
      onClose: onResetExportEmails,
      primaryAction: {
        content: exported ? 'Dismiss' : 'Start Export',
        disabled:
          loadingExport ||
          (!exported && impersonateIsLoggedIn && emails.length === 0),
        loading: loadingExport,
        onAction: exported ? onResetExportEmails : onExportEmails,
        withNestedModal: !exported,
      },
      title: 'Export to csv',
    });
  }, [
    emails,
    exported,
    impersonateIsLoggedIn,
    loadingExport,
    modal,
    onExportEmails,
  ]);

  const onExportToCsv = () =>
    modal.display(() => (
      <div>
        {impersonateIsLoggedIn && <>Enter emails address here:</>}
        {!impersonateIsLoggedIn && (
          <>
            Your CSV export will be emailed to {exportEmail} when complete. If
            you'd like to copy additional email addresses, enter them here:
          </>
        )}
        <TagsInput
          actionText="Add email address"
          onAddedTag={setEmails}
          onValidateInput={validateEmail}
          initialSelected={emails}
        />
      </div>
    ));

  const onResetExportEmails = () => {
    setEmails([]);
    setExported(false);
  };

  const handlePageSizeChange = (selected: string, _: string) => {
    setPageSize(selected);
    if (pageResultsProps.onPageSizeChange) {
      pageResultsProps.onPageSizeChange(selected);
    }
  };

  const isEmpty = tableProps.rows.length === 0;

  return (
    <LoadingSkeleton
      type={LoadingPageType.TABLE}
      loading={loading}
      pageTitle={skeletonTitle}
      hideSkeleton={tableProps.rows.length > 0}
    >
      <TableContainer className={className}>
        <Card>
          <DataTableContainer>
            <HeadContainer>
              <Heading element="h1">{title}</Heading>
              <FilterContainer>
                {filterProps && <Filters {...filterProps} />}
              </FilterContainer>
            </HeadContainer>
            {isEmpty && <EmptyState {...emptyState} />}
            {!isEmpty && <DataTable {...tableProps} />}
          </DataTableContainer>
        </Card>
        {!isEmpty && (
          <TableFooter>
            <FooterCell>
              <Select
                label="Results Per Page"
                labelInline={true}
                options={pageSizeOptions}
                onChange={handlePageSizeChange}
                value={pageSize}
                disabled={isEmpty}
              />
            </FooterCell>
            <FooterCell>
              <Pagination {...paginationProps} />
            </FooterCell>
            <FooterCell>
              {onExport && (
                <Button onClick={onExportToCsv} disabled={isEmpty}>
                  Export to csv
                </Button>
              )}
            </FooterCell>
          </TableFooter>
        )}
      </TableContainer>
    </LoadingSkeleton>
  );
};

export default Table;
